import React from 'react';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';

import { TrustLogo } from '@rover/icons';
import type { DisplayProps } from '@rover/kibble/core';
import { Box, Button, Display, Flex, Image, Link, Text } from '@rover/kibble/core';
import Section, { Props as SectionProps } from '@rover/kibble/patterns/Section';
import useRoverBrandContext from '@rover/react-lib/src/context/useRoverBrandContext';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import getBrandSpecificValue, {
  type BrandSpecificValueInput,
} from '@rover/shared/js/utils/getBrandSpecificValue';

import UnorderedList from '../../../components/lists/UnorderedList/UnorderedList';
import {
  HomepageBookSitterClick,
  InfoCardLearnMoreGuaranteeClick,
} from '../../../components/RoverProtectSignals/utils/analyticsEvents';
import {
  HOME_SUBMIT_BUTTON_TEXT,
  LEARN_MORE_HREF,
  LEARN_MORE_TEXT,
  SUPPORT_TEXT,
} from '../../../components/RoverProtectSignals/utils/RoverProtectSignals.constants';
import CatNeckRub from '../images/CatNeckRub.png';

type Props = SectionProps & {
  isGrooming?: boolean;
};

const HEADING_TEXT_COLORS: BrandSpecificValueInput<DisplayProps['textColor']> = {
  rover: 'display',
  catInAFlat: 'primary',
};

const LandingRoverProtect: React.FC<Props> = ({ isGrooming = false, ...sectionProps }) => {
  const { i18n } = useLingui();
  const { navigation } = useCountryConfigurationShim();
  const { brand } = useRoverBrandContext();

  const vetCareAmount = navigation?.formattedVetCareReimbursementAmount || '$25,000';

  // DEV-156190: Translate
  const ctaText = isGrooming ? 'Book a local groomer' : i18n._(HOME_SUBMIT_BUTTON_TEXT);
  const ctaHref = isGrooming ? '/grooming/search/' : '/search';

  return (
    <Section background="accent_secondary" sx={{ overflow: 'hidden' }} {...sectionProps}>
      <Flex flexDirection="row" gap="6x" alignItems="stretch">
        <Flex py="12x" flexDirection="column" flexGrow="1">
          {/* Needed to make the Logo vertically centered with heading text */}
          <Flex alignItems="center" gap="4x" mb="4x" flexDirection="row">
            <TrustLogo width={36} height={48} />
            <Display
              size="400"
              mb="0x"
              textColor={getBrandSpecificValue(HEADING_TEXT_COLORS, brand)}
            >
              <Trans>Book with peace of mind</Trans>
            </Display>
          </Flex>

          <UnorderedList
            listVariant="interactive"
            mb="6x"
            sx={{
              paddingInlineStart: 0,
              listStylePosition: 'inside',
              '& > li + li': { marginTop: '8px' },
            }}
          >
            <Text size="50">
              {isGrooming ? (
                // DEV-156190: Translate
                <>
                  <strong>Screened groomers</strong> have passed a third-party background check and
                  show verified reviews from other pet parents, like you.
                </>
              ) : (
                <Trans>
                  <strong>Screened pet sitters</strong> have passed a third-party background check
                  and show verified reviews from other pet parents, like you.
                </Trans>
              )}
            </Text>

            <Text size="50">
              <Trans>
                Every booking is backed by the Rover Guarantee, which includes up to {vetCareAmount}{' '}
                reimbursement for eligible vet care.
              </Trans>{' '}
              <Link
                size="50"
                href={LEARN_MORE_HREF}
                onPress={() => emitAnalyticsEvent(new InfoCardLearnMoreGuaranteeClick())}
              >
                {i18n._(LEARN_MORE_TEXT)}
              </Link>
            </Text>

            <Text size="50">{SUPPORT_TEXT()}</Text>
          </UnorderedList>

          <Flex gap="2x" flexDirection={['column', 'row']}>
            <Button
              variant="primary"
              size="small"
              onPress={() => emitAnalyticsEvent(new HomepageBookSitterClick())}
              href={ctaHref}
            >
              {ctaText}
            </Button>
          </Flex>
        </Flex>

        {/* Desktop image — out of flow so it doesn't drive row height; Box height comes from flex stretch */}
        <Box
          display={['none', 'block']}
          sx={{
            position: 'relative',
            flexShrink: 0,
            width: '384px',
            overflow: 'hidden',
            marginRight: '-60px',
          }}
        >
          <Image
            src={CatNeckRub}
            height="auto"
            width="100%"
            sx={{ position: 'absolute', top: '50%', right: 0, transform: 'translateY(-50%)' }}
            description=""
          />
        </Box>
      </Flex>
    </Section>
  );
};

export default LandingRoverProtect;
